import React from 'react';
import { Navigate, Link, useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Section, Card, Button } from '../components/ui';
import Header from './Header';
import Footer from './Footer';
import consultingHero from '../assets/images/service-consulting-hero.jpg';
import capitalHero from '../assets/images/service-capital-hero.jpg';
import technologyHero from '../assets/images/service-technology-hero.jpg';
import './ServiceLinePage.css';

const LINES = {
    consulting: {
        title: 'Consulting',
        subtitle: 'Change management that sticks in real workflows.',
        lede: 'We help leadership and delivery teams design and run transformation: operating model, adoption, governance, and integration—so strategy turns into behaviour, not only documentation.',
        bullets: [
            'Change and adoption planning anchored in how work actually gets done.',
            'Executive alignment, roadmaps, and governance your organisation can sustain.',
            'Workshops and facilitation when trade-offs need to be surfaced and decided.',
            'Support through critical milestones so momentum survives the first weeks after kick-off.',
        ],
        image: consultingHero,
        imageLabel: 'Workshop table with a process map on the wall',
    },
    capital: {
        title: 'Capital',
        subtitle: 'Angel investing and private equity with disciplined partnership.',
        lede: 'We back teams and opportunities where our sector judgment and operating experience add more than capital alone—whether as an angel co-investor or through private equity-style engagement, with clarity on milestones, risk, and governance.',
        bullets: [
            'Thesis-led conversations: fit, timing, and what “good” looks like before term sheets.',
            'Structured milestones and reporting that respect founders and fiduciary needs.',
            'Pattern recognition from parallel journeys—without imposing a generic playbook.',
            'Alignment between investment horizon, transformation agenda, and delivery reality.',
        ],
        image: capitalHero,
        imageLabel: 'Daylit review table with a bound document and ruler',
    },
    technology: {
        title: 'Technology',
        subtitle: 'Automation, AI, and analytics built for decisions—not demos.',
        lede: 'We design and deliver automation and AI solutions, and we develop analytics tools that make performance, risk, and opportunity visible when leadership needs to act—integrated with your change programme and data reality, not bolted on as shelfware.',
        bullets: [
            'Automation and AI scoped to outcomes: productivity, quality, compliance, and speed.',
            'Analytics products and dashboards aligned to decisions, not vanity metrics.',
            'Architecture and data hygiene that match how your teams actually work.',
            'Enablement so adoption is owned inside the organisation, with us alongside where useful.',
        ],
        image: technologyHero,
        imageLabel: 'Analytics desk with a calm dashboard on an ultrawide display',
    },
};

const ServiceLinePage = () => {
    const { slug } = useParams();
    const navigate = useNavigate();
    const line = LINES[slug];

    if (!line) {
        return <Navigate to="/services" replace />;
    }

    return (
        <div className="service-line-page">
            <Header variant="marketing" />
            <Section variant="large" className="service-line-hero anchor-target">
                <div className="service-line-hero-grid">
                    <motion.div
                        className="service-line-hero-copy"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                    >
                        <Link to="/services" className="service-line-back">
                            ← All services
                        </Link>
                        <p className="service-line-eyebrow">{line.title}</p>
                        <h1 className="service-line-title">{line.subtitle}</h1>
                        <p className="service-line-lede">{line.lede}</p>
                        <div className="service-line-hero-cta">
                            <Button variant="primary" size="lg" onClick={() => navigate('/contact')}>
                                Discuss {line.title}
                            </Button>
                            <Button variant="outline" size="lg" onClick={() => navigate('/about')}>
                                About MotionSpace
                            </Button>
                        </div>
                    </motion.div>
                    <motion.div
                        className="service-line-hero-visual"
                        initial={{ opacity: 0, scale: 0.98 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.55, delay: 0.08 }}
                    >
                        <div className="service-line-hero-image-frame">
                            <img src={line.image} alt={line.imageLabel} className="service-line-hero-image" />
                        </div>
                    </motion.div>
                </div>
            </Section>

            <Section variant="default" className="service-line-detail">
                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: '-80px' }}
                    transition={{ duration: 0.5 }}
                >
                    <Card variant="glass" className="service-line-bullets-card">
                        <h2 className="service-line-bullets-title">What working together can look like</h2>
                        <ul className="service-line-bullets">
                            {line.bullets.map((item) => (
                                <li key={item}>{item}</li>
                            ))}
                        </ul>
                    </Card>
                </motion.div>
            </Section>

            <Footer />
        </div>
    );
};

export default ServiceLinePage;
